import { useTranslate } from "@similarweb/i18n-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import type { CompanyCard } from "app/pages/sales-intelligence/modules/company-analysis/types/company";

import { CompanyDataItem } from "./CompanyDataItem";
import { DataSection, type DataSectionProps } from "./DataSection";
import { StyledCardBody, StyledVerticalLine } from "./styles";
import {
    baseFields,
    baseFieldsNoEcommerce,
    baseFieldsNoFounding,
    baseFieldsNoFoundingNoEcommerce,
    foundingFields,
    getEcommerceFields,
} from "./utils";

type Props = {
    data: CompanyCard;
    isEcommConversionRateEnabled: boolean;
};

type SectionConfig = Omit<DataSectionProps, "data"> & { id: string };

const renderDivider = (key: string) => (
    <Flex
        key={key}
        data-automation="section-divider"
        justifyContent="center"
        flex="auto"
        alignSelf="stretch"
    >
        <StyledVerticalLine />
    </Flex>
);

const renderWithDividers = (sections: readonly SectionConfig[], data: CompanyCard) =>
    sections.flatMap((section, i) => {
        const sectionNode = (
            <DataSection
                key={section.id}
                data={data}
                {...section}
            />
        );

        return i === 0 ? [sectionNode] : [renderDivider(`divider-${section.id}`), sectionNode];
    });

export const CompanyDataBody = ({ data, isEcommConversionRateEnabled }: Props) => {
    const { translate } = useTranslate();

    const isWithFounding = !!data.totalFundingAmount;
    const isWithEcommerce = !!data.isEcommerceSite;

    if (!isWithFounding && !isWithEcommerce) {
        return (
            <StyledCardBody
                alignItems="flex-start"
                justifyContent="space-between"
            >
                {baseFieldsNoFoundingNoEcommerce.map((field) => (
                    <CompanyDataItem
                        key={field.key}
                        fieldKey={field.key}
                        title={translate(`si.company-analysis.company-data.card.field.${field.key}`)}
                        badge={field.badge}
                        value={field.render ? field.render(data[field.key]) : (data[field.key] as string)}
                        annotation={field.getAnnotation?.({ data })}
                    />
                ))}
            </StyledCardBody>
        );
    }

    const sections: readonly SectionConfig[] =
        isWithFounding && !isWithEcommerce
            ? [
                  {
                      id: "base",
                      fields: baseFieldsNoEcommerce,
                      columnsNumber: 3,
                      flex: "0 0 50%",
                      gap: "40px 70px",
                  },
                  {
                      id: "founding",
                      fields: foundingFields,
                      columnsNumber: 2,
                      flex: "0 0 30%",
                      gap: "40px 70px",
                  },
              ]
            : !isWithFounding && isWithEcommerce
              ? [
                    {
                        id: "base",
                        fields: baseFieldsNoFounding,
                        columnsNumber: 3,
                        flex: "0 0 50%",
                        gap: "40px 70px",
                    },
                    {
                        id: "ecommerce",
                        fields: getEcommerceFields(isEcommConversionRateEnabled),
                        columnsNumber: isEcommConversionRateEnabled ? 3 : 2,
                        flex: "0 0 40%",
                        gap: "40px 70px",
                    },
                ]
              : [
                    {
                        id: "base",
                        fields: baseFields,
                        columnsNumber: 2,
                        flex: "0 0 23%",
                        minWidth: 274,
                        gap: "40px 32px",
                    },
                    {
                        id: "founding",
                        fields: foundingFields,
                        columnsNumber: 2,
                        flex: "0 0 25%",
                        minWidth: 278,
                        gap: "40px 32px",
                    },
                    {
                        id: "ecommerce",
                        fields: getEcommerceFields(isEcommConversionRateEnabled),
                        columnsNumber: isEcommConversionRateEnabled ? 3 : 2,
                        flex: "0 0 35%",
                        minWidth: 340,
                        gap: "40px 32px",
                    },
                ];

    return (
        <StyledCardBody
            alignItems="flex-start"
            gap={isWithFounding && isWithEcommerce ? "60px 10px" : "40px 10px"}
            flexWrap="wrap"
        >
            {renderWithDividers(sections, data)}
        </StyledCardBody>
    );
};
